import React, { useState, useEffect, useRef } from 'react';
import {
  ComplianceEntitiesAndIds,
  ComplianceReport,
  ComplianceReportApi,
} from 'types/complianceReport';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Collapse,
} from 'react-bootstrap';
import OSAHEADER from '../assets/images/osa-header.jpg';
import html2canvas from 'html2canvas';
import jsPDF from "jspdf";

interface EntityComplianceReportOverviewProps {
  entityComplianceData: ComplianceReportApi[];
  parentEntityName?: string;
  parentEntityId?: number | null;
}

const EntityComplianceReportOverview: React.FC<EntityComplianceReportOverviewProps> = ({
  entityComplianceData,
  parentEntityName,
}) => {
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
  const [complianceData, setComplianceData] = useState<ComplianceReport[]>([]);
  const [complianceEntitiesAndIds, setComplianceEntitiesAndIds] =
    useState<ComplianceEntitiesAndIds[]>([]);
  const [complianceEntities, setComplianceEntities] = useState<string[]>([]);
  const [nonProfitFilterChecked, setNonProfitFilterChecked] =
    useState<boolean>(false);
  const [
    nonProfitExcludedComplianceData,
    setNonProfitExcludedComplianceData,
  ] = useState<ComplianceReport[]>([]);
  const [
    nonProfitExcludedComplianceEntitiesAndIds,
    setNonProfitExcludedComplianceEntitiesAndIds,
  ] = useState<ComplianceEntitiesAndIds[]>([]);
  const [
    nonProfitExcludedComplianceEntities,
    setNonProfitExcludedComplianceEntities,
  ] = useState<string[]>([]);
  // const [loading, setLoading] = useState<boolean>(false);
  const [allEntities, setAllEntities] = useState<{ name: string; id: number }[]>(
    []
  );
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [selectedComplianceData, setSelectedComplianceData] = useState<
    ComplianceReport[]
  >([]);
  const irBodyRef = useRef<HTMLDivElement>(null);

  const formatEntityStringForDownload = (value: string) => {
    if (!value) return '';
    let finalString = value.toLowerCase().replaceAll(" ", "_")
    return finalString;
  }

  const findFiscalYearEnd = (fyDateString: string) => {
    const dateArray = fyDateString.split('-');
    return dateArray[1] === '06' && dateArray[2] === '30'
      ? 'July 1st to June 30'
      : 'January 1st to December 31st';
  };

  // Updated - compliance data mapping
  useEffect(() => {
    if (!entityComplianceData || entityComplianceData.length === 0) return;

    const mapped: ComplianceReport[] = entityComplianceData.map((d) => ({
      sf_id: String(d.sf_id),
      sf_name: d.sf_name ?? '',
      tax_holding_county: d.tax_holding_county ?? '',
      entity_status: d.entity_status ?? '',
      auto_transparency_hold: Number(d.auto_transparency_hold ?? 0),
      auto_transparency_delinquent: Number(d.auto_transparency_delinquent ?? 0),
      auto_hold_funds: Number(d.auto_hold_funds ?? 0),
      auto_suspend_funds: Number(d.auto_suspend_funds ?? 0),
      fy_end: d.fy_end ?? '',
      rvex_beg: (d as any).rvex_beg ?? (d as any).rvex_beg_date ?? (d as any).rvex_beg_string ?? '',
      py_beg: (d as any).py_beg ?? (d as any).py_beg_date ?? (d as any).py_beg_string ?? '',
      govt_lvl: d.govt_lvl ?? '',
      miss_exp: d.miss_exp ?? null,
      miss_rev: d.miss_rev ?? null,
      miss_pay: d.miss_pay ?? null,
      miss_lg: d.miss_lg ?? null,
      miss_dandi: d.miss_dandi ?? null,
    }));

    setComplianceData(mapped);
  }, [entityComplianceData]);

  // Updated - compliance data passed down
  useEffect(() => {
    if (complianceData.length === 0) return;

    let defaultEntityName: string | undefined;

    if (
      parentEntityName &&
      complianceData.some((e) => e.sf_name === parentEntityName)
    ) {
      defaultEntityName = parentEntityName;
    } else if (nonProfitFilterChecked) {
      defaultEntityName = complianceData[0]?.sf_name;
    } else {
      const nonExcluded = complianceData.find(
        (e) => e.govt_lvl !== 'Non Profits' && e.govt_lvl !== 'State Agency'
      );
      defaultEntityName = nonExcluded?.sf_name;
    }

    if (!defaultEntityName) return;

    const initialSelection = complianceData
      .filter((entity) => entity.sf_name === defaultEntityName)
      .map((entity) => ({
        ...entity,
        rvex_beg: entity.rvex_beg || null,
        py_beg: entity.py_beg || null,
      }));

    setSelectedComplianceData(initialSelection);
    setSelectedEntities([defaultEntityName]);
  }, [complianceData, nonProfitFilterChecked, parentEntityName]);

  useEffect(() => {
    if (complianceData.length === 0) return;

    const complianceEntityIDsAndNames = complianceData
      .map((entity) => ({
        sf_id: entity.sf_id,
        sf_name: entity.sf_name,
      }))
      .sort((a, b) => a.sf_name.localeCompare(b.sf_name));

    const nonProfitExcludedComplianceEntityIDsAndNames = complianceData
      .filter(
        (entity) =>
          entity.govt_lvl !== 'Non Profits' &&
          entity.govt_lvl !== 'State Agency'
      )
      .map((entity) => ({
        sf_id: entity.sf_id,
        sf_name: entity.sf_name,
      }))
      .sort((a, b) => a.sf_name.localeCompare(b.sf_name));

    const complianceEntities = complianceEntityIDsAndNames.map(
      (entity) => entity.sf_name
    );

    const nonProfitExcludedComplianceEntities =
      nonProfitExcludedComplianceEntityIDsAndNames.map(
        (entity) => entity.sf_name
      );

    setComplianceEntitiesAndIds(complianceEntityIDsAndNames);
    setNonProfitExcludedComplianceEntitiesAndIds(
      nonProfitExcludedComplianceEntityIDsAndNames
    );
    setComplianceEntities(complianceEntities);
    setNonProfitExcludedComplianceEntities(
      nonProfitExcludedComplianceEntities
    );
  }, [complianceData]);

  const options = nonProfitFilterChecked
  ? complianceEntities
      .map((e) => ({ value: e, label: e }))
      .some((o) => o.value === parentEntityName)
    ? complianceEntities.map((e) => ({ value: e, label: e }))
    : [{ value: parentEntityName!, label: parentEntityName! }, ...complianceEntities.map((e) => ({ value: e, label: e }))]
  : nonProfitExcludedComplianceEntities.map((e) => ({ value: e, label: e }));

  const defaultValue = parentEntityName
    ? { value: parentEntityName, label: parentEntityName }
    : options[0];

  const downloadIndividualReport = () => {
    if (!irBodyRef.current) return;
    
    const htmlWidth = $("#ir-body").width() ?? 0;
    const htmlHeight = $("#ir-body").height() ?? 0;
    const topLeftMargin = 15;
    const pdfWidth = htmlWidth + (topLeftMargin * 2);
    const pdfHeight = (pdfWidth * 1.5) + (topLeftMargin * 2);
    const canvasImageWidth = htmlWidth;
    const canvasImageHeight = htmlHeight;
    const entity_selected = $('#ir-title').text().toLowerCase().replaceAll(" ","-").replaceAll("/","-");
    const totalPDFPages = Math.ceil(htmlHeight / pdfHeight) - 1;

    html2canvas($("#ir-body")[0],{allowTaint:true}).then(function(canvas: HTMLCanvasElement) {
      canvas.getContext('2d');

      let imgData = canvas.toDataURL("image/jpeg", 1.0);
      let pdf = new jsPDF('p', 'pt', [pdfWidth, pdfHeight]);
      pdf.addImage(imgData, "JPG", topLeftMargin, topLeftMargin, canvasImageWidth, canvasImageHeight);

      for (let i of Array(totalPDFPages).keys()) {
        pdf.addPage([pdfWidth, pdfHeight]);
        pdf.addImage(
          imgData,
          'JPG',
          topLeftMargin,
          -(pdfHeight * (i + 1)) + (topLeftMargin * 4),
          canvasImageWidth,
          canvasImageHeight
        );
      }

      const fileName = `${formatEntityStringForDownload(defaultValue?.value ?? 'entity')}_compliance_report.pdf`;
      pdf.save(fileName);
    });
  }

  return (
    <div className="entity-compliance-report-overview">
      <Row className="mb-2">
        <Col xs={12}>
          <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
            <Card.Body style={{ justifyItems: "center" }}>
              <div className="tu_tabHeader">
                <div className="tu-tabHeader__titleRow">
                  <h1 className="tu-tabHeader__title">
                  Compliance Report</h1>
                  <button
                    type="button"
                    className="tu-tabHeader__infoBtn"
                    onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                    aria-label="Compliance Report info"
                  >
                    <i className="fas fa-info"/>
                  </button>
                </div>
              </div>

              <Collapse in={showHeaderInfo}>
                <div>
                  <p className="lead mt-2 text-center">
                    The compliance report tab shows reporting and compliance for individual entities. This includes verification of auditor reports, deposit and investment reports, transparency reports, and overall accuracy as certified by the Office of the Utah State Auditor.
                  </p>
                </div>
              </Collapse>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {entityComplianceData.length > 0 ? (
        <Card className="mb-4">
          <Row className="px-4">
            <Col md={7} className="pt-2">
              <Alert variant="secondary" className="text-center">
                Data is updated on the hour from (6am to 6pm).
              </Alert>
            </Col>

            <Col md="auto" className="pt-2">
              <Button
                size="lg"
                variant="secondary"
                href="https://auditor.utah.gov/local-government/"
                target="_blank"
              >
                Visit Local Government Resource Center
              </Button>
            </Col>
          </Row>

          <Row className="border-info rounded mx-1 mt-3">
            <Col md={8} className="pt-3">
              <div id="ir-body" ref={irBodyRef}>
              <Card className="mb-3">
                <Card.Header className="bg-secondary" 
                style={{ borderBottom: 'none'}}
                >
                  <div className="d-flex justify-content-between">
                    <h4 className="text-white text-uppercase">
                      Review Compliance Report
                    </h4>
                    <Button 
                      // variant="primary"
                      variant="secondary"
                      id="ir-download-button" 
                      onClick={downloadIndividualReport}>Download</Button>
                  </div>
                </Card.Header>
                
                <Card.Body>
                  <img className="" src={OSAHEADER} alt="OSA-Header" style={{ width: '100%' }} />

                  {selectedComplianceData.length > 0 &&
                    selectedComplianceData.map((entity) => (
                      <div key={entity.sf_id}>
                        <h1 className="lead">{entity.sf_name}</h1>
                        <ul>
                          <li className="mb-1">
                            <span className="text-muted text-uppercase">
                              Government Type:
                            </span>{' '}
                            <span className="badge"
                              style={{backgroundColor: "	#1e90ff"}}
                            >
                              {entity.govt_lvl}
                            </span>
                          </li>

                          <li className="mb-1">
                            <span className="text-muted text-uppercase">
                              Status:
                            </span>{' '}
                            {entity.entity_status === "On hold" ? (
                              <span className="badge" style={{ backgroundColor: "#dc3545", color: "white" }}>
                                {entity.entity_status}
                              </span>
                            ) : entity.entity_status === "Delinquent" ? (
                              <span className="badge" style={{ backgroundColor: "#FB4F14", color: "white" }}>
                                {entity.entity_status}
                              </span>
                            ) : (
                              <span className="badge badge-secondary">
                                {entity.entity_status}
                              </span>
                            )}
                            
                          </li>

                          <li className="mb-1">
                            <span className="text-muted text-uppercase">
                              Fiscal Year Spans:
                            </span>{' '}
                            <span className="badge"
                              style={{ backgroundColor: "#28a745"}}
                            >
                              {findFiscalYearEnd(entity.fy_end)}
                            </span>
                          </li>

                          <li className="mb-1">
                            <span className="text-muted text-uppercase">
                              Began Reporting Rev./Exp:
                            </span>{' '}
                            {entity.rvex_beg && String(entity.rvex_beg).trim() !== '' ? (
                              <span className="badge badge-secondary">
                                {String(entity.rvex_beg)}
                              </span>
                            ) : (
                              <span className="text-uppercase badge"
                              style={{ backgroundColor: "#ffc107"}}
                              >
                                Not Set
                              </span>
                            )}
                          </li>

                          <li className="mb-1">
                            <span className="text-muted text-uppercase">
                              Began Reporting Payroll:
                            </span>{' '}
                            {entity.py_beg && String(entity.py_beg).trim() !== '' ? (
                              <span className="badge badge-secondary">
                                {String(entity.py_beg)}
                              </span>
                            ) : (
                              <span className="text-uppercase badge badge-danger">
                                Not Set
                              </span>
                            )}
                          </li>
                        </ul>
                      </div>
                    ))}
                  {/* </div> */}
                </Card.Body>
              </Card>

              <Card className="mb-3">
                {selectedComplianceData.length > 0 &&
                  (selectedComplianceData[0].miss_lg == null ? (
                    <>
                      <Card.Header className="bg-secondary" 
                        style={{ borderBottom: 'none'}}
                        >
                      <h3 className="mb-0 text-white text-uppercase">Auditor Reports</h3>
                      </Card.Header>
                      <Card.Body>
                        <span>
                          All <strong>Auditor</strong> reports are up to date.
                        </span>
                      </Card.Body>
                    </>
                  ) : (
                    <>
                      <Card.Header className="bg-danger" 
                          style={{ borderBottom: 'none'}}
                          >
                        <h3 className="mb-0 text-white text-uppercase">Auditor Reports</h3>
                        </Card.Header>
                      <Card.Body>
                        The following <strong>Auditor</strong> reports are delinquent:
                        <br/>
                        <span>
                          <ul className="mt-2">
                            {selectedComplianceData[0].miss_lg
                              ?.split(', ')
                              .map((report, index) => (
                                <li key={index}>{report}</li>
                              ))}
                          </ul>
                        </span>
                      </Card.Body>
                    </>
                  ))}
              </Card>

              <Card className="mb-3">
                {selectedComplianceData.length > 0 &&
                  (selectedComplianceData[0].miss_dandi == null ? (
                    <>
                      <Card.Header className="bg-secondary" style={{ borderBottom: 'none'}}>
                      <h3 className="mb-0 text-white text-uppercase">
                        Deposit and Investment Reports
                      </h3>
                    </Card.Header>
                    <Card.Body>
                      <span>
                        All <strong>Deposit and Investment</strong> reports are up to date.
                      </span>
                    </Card.Body>
                    </>
                  ) : (
                    <>
                      <Card.Header className="bg-danger" style={{ borderBottom: 'none'}}>
                        <h3 className="mb-0 text-white text-uppercase">
                          Deposit and Investment Reports
                        </h3>
                      </Card.Header>
                      <Card.Body>
                        The following quarters of <strong>Deposit and Investment</strong> are delinquent:
                        <span>
                          <ul className="mt-2">
                            {selectedComplianceData[0].miss_dandi
                              ?.split(', ')
                              .map((report, index) => (
                                <li key={index}>{report}</li>
                              ))}
                          </ul>
                        </span>
                      </Card.Body>
                    </>
                  ))}
              </Card>

              {/* Updated version with better null/undefined checks - Missing Revenue section */}
              <Card>
                {selectedComplianceData?.[0] ? (
                  <>
                    {(
                      selectedComplianceData[0].miss_rev == null ||
                      selectedComplianceData[0].miss_rev === ""
                    ) ? (
                      <>
                        <Card.Header className="bg-secondary text-white" style={{ borderBottom: 'none' }}>
                          <h3 className="mb-0 text-white">Transparency Reports</h3>
                        </Card.Header>
                        <Card.Body style={{ paddingBottom: '0' }}>
                          All <strong>Revenue</strong> reports are up to date.
                        </Card.Body>
                      </>
                    ) : (
                      <>
                        <Card.Header className="bg-danger text-white" style={{ borderBottom: 'none' }}>
                          <h3 className="mb-0 text-white">Transparency Reports</h3>
                        </Card.Header>
                        <Card.Body style={{ paddingBottom: '0' }}>
                          The following quarters of <strong>Revenue</strong> are delinquent:
                          <span>
                            <ul className="mt-2">
                              {(selectedComplianceData[0].miss_rev || "")
                                .split(', ')
                                .filter(Boolean)
                                .map((report, index) => (
                                  <li key={index}>{report}</li>
                                ))}
                            </ul>
                          </span>
                        </Card.Body>
                      </>
                    )}

                    {(
                      selectedComplianceData[0].miss_exp == null ||
                      selectedComplianceData[0].miss_exp === ""
                    ) ? (
                      <Card.Body style={{ paddingTop: '0', paddingBottom: '0' }}>
                        All <strong>Expense</strong> reports are up to date.
                      </Card.Body>
                    ) : (
                      <Card.Body style={{ paddingTop: '0', paddingBottom: '0' }}>
                        The following quarters of <strong>Expense</strong> are delinquent:
                        <span>
                          <ul className="mt-2">
                            {(selectedComplianceData[0].miss_exp || "")
                              .split(', ')
                              .filter(Boolean)
                              .map((report, index) => (
                                <li key={index}>{report}</li>
                              ))}
                          </ul>
                        </span>
                      </Card.Body>
                    )}

                    {(
                      selectedComplianceData[0].miss_pay == null ||
                      selectedComplianceData[0].miss_pay === ""
                    ) ? (
                      <Card.Body style={{ paddingTop: '0' }}>
                        All <strong>Compensation</strong> reports are up to date.
                      </Card.Body>
                    ) : (
                      <Card.Body style={{ paddingTop: '0' }}>
                        The following quarters of <strong>Compensation</strong> are delinquent:
                        <div>
                          <ul className="mt-2">
                            {(selectedComplianceData[0].miss_pay || "")
                              .split(', ')
                              .filter(Boolean)
                              .map((report, index) => (
                                <li key={index}>{report}</li>
                              ))}
                          </ul>
                        </div>
                      </Card.Body>
                    )}
                  </>
                ) : (
                  <Card.Body>
                    <em>No compliance data selected.</em>
                  </Card.Body>
                )}
              </Card>
              </div>
            </Col>

            {/* Help / Resources */}
            <Col md={4} className="pt-3">
              <Card>
                <Card.Header className="bg-secondary text-white"
                style={{ borderBottom: 'none'}}
                >
                  <h4 className="text-white text-uppercase">Help / Resources</h4>
                </Card.Header>

                <Card.Body>
                  <Card className="mb-3">
                    <Card.Header className="bg-secondary text-white text-uppercase"
                    style={{ borderBottom: 'none'}}
                    >
                      Contact Office of the State Auditor
                    </Card.Header>
                    <Card.Body>
                      <strong className="text-uppercase">Auditor Reports:</strong>
                      <ul>
                        <li>
                          <div className="d-flex align-items-center gap-2">
                            Brooke Russell
                            <i className="fa-solid fa-envelope text-secondary" aria-hidden="true" />
                            <a
                              href="mailto:brookerussell@utah.gov"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Email Brooke Russell"
                            >
                              brookerussell@utah.gov
                            </a>
                          </div>
                        </li>
                        <li>
                          <div className="d-flex align-items-center gap-2">
                            Parker Van Eerden
                            <i className="fa-solid fa-envelope text-secondary" aria-hidden="true" />
                            <a
                              href="mailto:pvan@utah.gov"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Email Parker Van Eerden"
                            >
                              pvan@utah.gov
                            </a>
                          </div>
                        </li>
                        <li>
                          <div className="d-flex align-items-center gap-2">
                            Hadley Scott
                            <i className="fa-solid fa-envelope text-secondary" aria-hidden="true" />
                            <a
                              href="mailto:hscott@utah.gov"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Email Hadley Scott"
                            >
                              hscott@utah.gov
                            </a>
                          </div>
                        </li>
                        <li>
                          <div className="d-flex align-items-center gap-2">
                            Seth Oveson
                            <i className="fa-solid fa-envelope text-secondary" aria-hidden="true" />
                            <a
                              href="mailto:soveson@utah.gov"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Email Seth Oveson"
                            >
                              soveson@utah.gov
                            </a>
                          </div>
                        </li>
                      </ul>

                      <hr className="mb-4 mt-4" />
                      <strong className="text-uppercase">Transparency Reporting:</strong>
                      
                      <ul>
                        <li>
                          <div className="d-flex align-items-center gap-2">
                            Julien Counts
                            <i className="fa-solid fa-envelope text-secondary" aria-hidden="true" />
                            <a
                              href="mailto:jcounts@utah.gov"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Email Julien Counts"
                            >
                              jcounts@utah.gov
                            </a>
                          </div>
                        </li>
                        <li>
                          <div className="d-flex align-items-center gap-2">
                            Kramer McCausland
                            <i className="fa-solid fa-envelope text-secondary" aria-hidden="true" />
                            <a
                              href="mailto:kmccausland@utah.gov"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Email Kramer McCausland"
                            >
                              kmccausland@utah.gov
                            </a>
                          </div>
                        </li>
                        <li>
                          <div className="d-flex align-items-center gap-2">
                            Alex Nielson
                            <i className="fa-solid fa-envelope text-secondary" aria-hidden="true" />
                            <a
                              href="mailto:alexnielson@utah.gov"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Email Alex Nielson"
                            >
                              alexnielson@utah.gov
                            </a>
                          </div>
                        </li>
                      </ul>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3">
                    <Card.Header className="bg-secondary text-white text-uppercase"
                    style={{ borderBottom: 'none'}}
                    >
                      Contact Office of the State Treasurer
                    </Card.Header>
                    <Card.Body>
                      Candace Castor
                      <br />
                      <i className="fa-solid fa-envelope text-secondary" aria-hidden="true" />
                      <a
                        href="mailto:ccastor@utah.gov"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Email Candace Castor"
                      > ccastor@utah.gov
                      </a>
                      <br />
                      <a href="tel:8015381883">
                        <i className="fa-solid fa-phone"></i> 801-538-1883
                      </a>
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header className="bg-secondary text-white text-uppercase"
                    style={{ borderBottom: 'none'}}
                    >
                      Where do I Find Helpful Resources to Complete my Reporting?
                    </Card.Header>
                    <Card.Body>
                      Resources for reporting can be found at the <a style={{fontWeight: "700"}} href="https://auditor.utah.gov/local-government/" target="_blank" rel="noopener">local government resource center</a>.
                    </Card.Body>
                  </Card>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card>
      ) : (
        <Card className="mb-4" style={{}}>
          <div className="p-5 text-center">
            <div className="alert alert-light shadow-sm">
              No data available
            </div>
          </div>
        </Card>
      )}

      {/* Alert + Link */}
    </div>
  );
};

export default EntityComplianceReportOverview;